import { capture } from '@/handlers/error';
import { useOutsideClick } from '@/hooks/useOutsideClick';
import { ResizeBorder } from '@/pages/LessonEditor/components/ResizeBorder';
import { useComponentEditor } from '@/providers/editor/EditorProvider';
import { Coordinate, isSlideComponentSasFillInTheBlanksWriting, PropsWithPreview, Size, SlideComponent } from '@/types/slide';
import ImageIcon from '@mui/icons-material/Image';
import { Box } from '@mui/material';
import { useNotifications } from '@toolpad/core';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ComponentLabel } from '../ComponentLabel';
import { useRightClickMenu } from '../hooks/useRightClickMenu';
import { useSlideComponent } from '../hooks/useSlideComponent';
import { RightClickMenu } from '../RightClickMenu';
import { SlideRef } from '../Slide';

interface Props {
    ref: React.RefObject<HTMLDivElement | null>;
    init: {
        model: SlideComponent;
        focus?: boolean;
    };
    slideRef: React.RefObject<SlideRef | null>;
}

export const SlideComponentSasFillInTheBlanksWriting: React.FC<PropsWithPreview<Props>> = ({ ref, init, preview, slideRef }) => {
    const { t } = useTranslation();
    const notifications = useNotifications();
    const { bringToFront, bringToBack, copyComponent, pasteComponent, undo, redo } = useComponentEditor();

    const { rightClickMenu, handleRightClickMenu, rightClickMenuClose } = useRightClickMenu();

    const { isSelected } = useSlideComponent(ref, [], !!preview);

    const [model] = useState<SlideComponent>({ ...init.model });
    const [isFocus, setIsFocus] = useState<boolean>(init.focus ?? false);

    const [tSize] = useState<Size>(model.size);
    const [tPos] = useState<Coordinate>(model.pos);

    const [aspectRatio] = useState(model.size.width / model.size.height);

    const isEditing = isFocus || isSelected;

    const onBringToFrontHandler = React.useCallback(
        async (id: string) => {
            try {
                await bringToFront(id);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [bringToFront, notifications],
    );

    const onBringToBackHandler = React.useCallback(
        async (id: string) => {
            try {
                await bringToBack(id);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [bringToBack, notifications],
    );

    useOutsideClick(ref, () => {
        if (preview) return;
        if (!ref) return;

        setIsFocus(false);
    });

    if (!isSlideComponentSasFillInTheBlanksWriting(model)) {
        console.error(`[SlideComponentSasFillInTheBlanksWriting] ${JSON.stringify(model)} is not SlideComponentSasFillInTheBlanksWriting model`);
        return null;
    }

    const isPlaceholder = !model.content.imageURL && !model.content.image;

    return (
        <Box
            onContextMenu={isEditing ? handleRightClickMenu : undefined}
            ref={ref}
            id='sasFillInTheBlanksWriting-component'
            sx={{
                position: 'absolute',
                left: tPos.x,
                top: tPos.y,
                m: 'inherit',
                p: 'inherit',
                textAlign: 'left',
                width: tSize.width,
                height: tSize.height,
            }}
            onClick={(e) => {
                if (!preview) {
                    e.stopPropagation();
                }
            }}
        >
            <ResizeBorder resizable={false} disabled size={tSize} pos={tPos} preview={preview} isSelected={isEditing} aspectRatio={aspectRatio} slideRef={slideRef}>
                {!preview && (
                    <ComponentLabel
                        variant='caption'
                        sx={{
                            ...(!isEditing && {
                                opacity: 0, // Hide by default
                                transition: 'opacity 0.2s ease',
                                '#sasFillInTheBlanksWriting-component:hover &': {
                                    opacity: 1, // Show when text-component is hovered
                                },
                            }),
                        }}
                    >
                        {t('slide.legacy.sasFillInTheBlanksWriting.title')}
                    </ComponentLabel>
                )}

                <Box
                    id='sasFillInTheBlanksWriting-component'
                    sx={{
                        m: 'inherit',
                        p: 'inherit',
                        width: '100%',
                        height: '100%',
                        aspectRatio: 'auto',
                        transform: `scaleX(${1})`,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    {isPlaceholder ? (
                        <Box
                            sx={{
                                backgroundColor: (theme) => (theme.vars || theme).palette.grey[200],
                                width: '100%',
                                height: '100%',
                                aspectRatio: 'auto',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <ImageIcon fontSize='large' />
                        </Box>
                    ) : (
                        <Box
                            component='img'
                            src={model.content.imageURL ? model.content.imageURL : `/asset/characters/${model.content.image}/image.png`}
                            sx={{
                                width: '100%',
                                height: '100%',
                                aspectRatio: 'auto',
                            }}
                            width={model.size.width}
                        />
                    )}
                </Box>
            </ResizeBorder>

            <RightClickMenu
                componentId={model.id}
                contextMenu={rightClickMenu}
                handleClose={rightClickMenuClose}
                bringToFront={onBringToFrontHandler}
                bringToBack={onBringToBackHandler}
                copyComponent={copyComponent}
                pasteComponent={pasteComponent}
                undo={undo}
                redo={redo}
            />
        </Box>
    );
};
